import React, { useEffect, useState } from 'react'
import { assoc, assocPath, compose, path, pathOr } from 'ramda'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Colors,
  ChartOptions,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import axios from 'axios'
import { LoadingOverlay } from '@mantine/core'
import classes from './Leaderboards.module.scss'
import { dataLabelAlignment, formatScientificNotation } from './utils'

interface LeaderBoardResponse {
  data: {
    score: number
    name: string
  }[]
}

const LeaderboardCache = {}

const getCachedLeaderBoard = (type, start, end): LeaderBoardResponse | null => {
  const { res, date} = LeaderboardCache[`${type}-${start}-${end}`] || {}
  if (!res || +new Date() > (+date + 1000 * 60 * 3)) {
    delete LeaderboardCache[`${type}-${start}-${end}`]
    return null
  }
  return res
}

const setCachedLeaderBoard = (type, start, end, res): void => {
  LeaderboardCache[`${type}-${start}-${end}`] = {res, date: new Date()}
}

export interface ChartProps {
    title?: string
    subtitle?: string
    height: number
    data: {
      title?: string
      subtitle?: string
      labels: string[]
      datasets: [
        {
          data: number[]
          borderColor: string[]
          barThickness: number
          backgroundColor: string[]
        },
      ]
    }
    options: ChartOptions<'bar'> & {
      scales: ChartOptions['scales'] & {
        y: ChartOptions['scales']['y'] & {
          labelWidth: boolean
        }
      }
      plugins: {
        datalabels: ChartOptions<'bar'>['plugins']['datalabels'] & {
          postfix?: string
        }
      }
    }
    leaderboardType?: string
    fromDateTime?: string
    toDateTime?: string
  }
  
  export interface ChartContent extends ChartProps {
    __typename: 'Chart'
  }
  
  const renderIconOnDataLabel = {
    id: 'renderIconOnDataLabel',
    afterDraw: (chart, args, {image, imageWidth, imageHeight, width, height, xOffset, yOffset}) => {
      if (!image || !imageWidth || !imageHeight) {
        return
      }
      const { ctx } = chart
      const xAxis = chart.scales.x
      const yAxis = chart.scales.y
      ctx.save();
      if (!chart.img) {
        chart.img = chart.img || new Image; 
        chart.img.src = image
        chart.img.className = classes.slideInAnmation
      }
      yAxis.ticks.forEach((_, index) => {
        const currentXPosition = chart.$datalabels._labels[yAxis.ticks.length - (index + 1)]._el.x
        const value = Number(chart.config._config.data.datasets[0].data[index])
        const barWidth = xAxis.getPixelForValue(value);
        const labelWidth = chart.$datalabels._labels[yAxis.ticks.length - (index + 1)]._rects.text.w;
        const maxlabelWidth = formatScientificNotation(value).length * 15
        const x = (barWidth < maxlabelWidth ? labelWidth + 8  : currentXPosition) + xOffset
        const y = yAxis.getPixelForTick(index) + yOffset
        xAxis.ctx.drawImage(chart.img, 0, 0, imageWidth, imageHeight, x, y, width || imageWidth, height || imageHeight)
      });
      ctx.restore();
    },
    defaults: {
      xOffset: 0,
      yOffset: 0
    }
  }
  
  ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Colors,
    ChartDataLabels,
    renderIconOnDataLabel
  )
  

export const LeaderBoardChart = ({ options, data, height, leaderboardType, fromDateTime, toDateTime, title, subtitle }: ChartProps): JSX.Element => {
    const [windowWidth, setWindowWidth] = useState<number>()
    const [chartData, setChartData] = useState<ChartProps['data']>(data)
    const [loading, setLoading] = useState(false)
    const subTitle = subtitle || chartData.subtitle
  
    useEffect(() => {
      function handleResize(): void {
        setWindowWidth(window.innerWidth)
      }
  
      // Add event listener
      window.addEventListener('resize', handleResize)
  
      // Call handler right away so state gets updated with initial window size
      handleResize()
  
      // Remove event listener on cleanup
      return () => window.removeEventListener('resize', handleResize)
    }, [])
  
    useEffect(()=> {
      const cachedResponse = getCachedLeaderBoard(leaderboardType, fromDateTime, toDateTime)
      if (leaderboardType && fromDateTime && toDateTime) {
        setLoading(true);
        (cachedResponse ? Promise.resolve(cachedResponse) : axios
          .get(`${process.env.REACT_APP_LEADERBOARD_API_URL}/api/leaderboards/${fromDateTime}/${toDateTime}/${leaderboardType}`))
          .then((res: LeaderBoardResponse) => {
            const { names, scores } = res.data.reduce(
              (previous, { name, score }) => {
                previous.names.push(name)
                previous.scores.push(score)
                return previous
              },
              {
                names: [],
                scores: [],
              } as { names: string[]; scores: number[] },
            )
        
            const newData = compose(
              (d: ChartProps['data']) => {
                let mutatedData = assoc('datasets', d.datasets || [], d)
                mutatedData = assocPath(['datasets', 0], mutatedData.datasets[0] || {}, mutatedData)
                mutatedData = assocPath(['datasets', 0, 'data'], scores, mutatedData)
                return mutatedData
              },
              assoc('labels', names),
            )(data)
            setCachedLeaderBoard(leaderboardType, fromDateTime, toDateTime, res)
            setChartData(newData)
          })
          .finally(()=> {
            setTimeout(()=> {
              setLoading(false)
            })
          })
      }
    }, [leaderboardType, fromDateTime, toDateTime])
  
    const MAX_LABEL_CHARACTERS = windowWidth
      ? Math.min(Math.max(Math.ceil(windowWidth / 50), 10), 24)
      : 10
  
    const barThickness = pathOr(50, ['datasets', '0', 'barThickness'], chartData)
    const dataLength = pathOr(0, ['datasets', '0', 'data', 'length'], chartData)
    const actualHeight = dataLength ? Math.min(1.2 * (barThickness + 50) * dataLength + 130, height) : height
  
    if (options.scales?.y?.labelWidth) {
      options = assocPath(['scales', 'y', 'afterFit'], (scaleInstance) => {
        scaleInstance.width = MAX_LABEL_CHARACTERS * 12.5
      }, options)
    }
    options = assocPath(['plugins', 'tooltip', 'enabled'], true, options)
    if (options.plugins.datalabels) {
      const postfix = options.plugins.datalabels.postfix || ''
      if (postfix) {
        options = assocPath(
          ['plugins', 'tooltip', 'callbacks', 'label'],
          ({ formattedValue }) => formattedValue + postfix,
          options,
        )
      }
      if (options.plugins.datalabels.align === 'start') {
        options.plugins.datalabels = {
          ...options.plugins.datalabels,
          align: ({ chart, dataIndex, dataset }) => {
            return dataLabelAlignment(chart, dataIndex, dataset, 'center', 'start', postfix)
          },
          anchor: ({ chart, dataIndex, dataset }) => {
            return dataLabelAlignment(chart, dataIndex, dataset, 'center', 'end', postfix)
          },
        }
      } else if (Number(options.plugins.datalabels.align) >= 90) {
        const align = Number(options.plugins.datalabels.align)
        options.plugins.datalabels = {
          ...options.plugins.datalabels,
          align: ({ chart, dataIndex, dataset }) => {
            const barWidth = chart.scales.x.getPixelForValue(Number(dataset.data[dataIndex]))
            const labelWidth = (formatScientificNotation(Number(dataset.data[dataIndex])) + postfix).trim().length * 15
            if (barWidth < labelWidth) {
              return 45
            }
            return align
          },
          anchor: ({ chart, dataIndex, dataset }) => {
            const barWidth = chart.scales.x.getPixelForValue(Number(dataset.data[dataIndex]))
             const labelWidth = (formatScientificNotation(Number(dataset.data[dataIndex])) + postfix).trim().length * 15
            if (barWidth < labelWidth) {
              return 'start'
            }
            return 'end'
          },
          padding: ({ chart, dataIndex, dataset }) => {
            const barWidth = chart.scales.x.getPixelForValue(Number(dataset.data[dataIndex]))
             const labelWidth = (formatScientificNotation(Number(dataset.data[dataIndex])) + postfix).trim().length * 15
            if (barWidth < labelWidth) {
              return {top: 2, left: -20}
            }
            return 0
          },
        }
      }
      options.plugins.datalabels = {
        ...options.plugins.datalabels,
        formatter: (value, context) => {
          return formatScientificNotation(value) + postfix
        }
      }
    }
  
    return (
      <div
        className={classes.chartContainer}
        style={{
          zIndex: 3,
          overflowX: 'visible',
        }}
      >
        {subTitle && (
          <div className={classes.chartSubtitle}>{subTitle}</div>
        )}
        <div style={{ maxWidth: '100%', height: `${actualHeight}px`, zIndex: 5, position: 'sticky', marginTop: '30px', backgroundColor: 'rgba(4, 29, 31, 0.6)', paddingLeft: '30px', paddingBottom: '20px' }}>
          {loading ? <LoadingOverlay visible={loading} zIndex={1000} overlayColor='rgba(4, 29, 31, 0.6)' loaderProps={{color: '#81BEC1'}} /> : <Bar
            style={{ maxWidth: '100%', overflow: 'visible' }}
            options={options}
            data={chartData}
          /> }
        </div>
      </div>
    )
  }